<template>
  <div
    v-if="contentModel !== null || vueModel !== null"
    id="quickquote-house-step-1-component"
    data-test="quickquote-house-step-1"
  >
    <!-- Husforsikring -->
    <h1 v-html="contentModel.SharedFields.LabelHeadingStep1" />
    <section>
      <article class="flow-group">
        <div class="control">
          <!-- Adresse -->
          <h3 data-test="quickquote-house-headline-address">
            <span v-html="contentModel.LabelFindHouse" />
            <tooltip
              v-if="contentModel.TooltipFindHouse !== null"
              :content="contentModel.TooltipFindHouse"
            />
          </h3>
          <Field
            :rules="'required'"
            v-model="vueModel.TemplateModel.SelectedAddressTemplate"
            label="Adresse"
            name="address"
          >
            <address-search
              v-model="vueModel.TemplateModel.SelectedAddressTemplate"
              :init-value="vueModel.TemplateModel.SelectedAddressTemplate"
              data-test="quickquote-house-select-address"
              :placeholder="contentModel.PlaceholderAddressTypeaheadBox"
              name="address"
              class="v-select-search-icon"
              @click.native="activityInAddressField"
            />
          </Field>
          <ValidationError name="address" />

          <!-- BBR -->
          <div v-if="houseFound" data-test="quickquote-house-bbr-details" class="bbr-section">
            <div class="row">
              <div class="small-12 columns">
                <h3 data-test="quickquote-house-headline-correct-property-information">
                  <span v-html="contentModel.LabelCorrectPropertyInformation" />
                  <tooltip
                    v-if="contentModel.TooltipCorrectPropertyInformation !== null"
                    :content="contentModel.TooltipCorrectPropertyInformation"
                  />
                </h3>
                <label
                  v-if="contentModel.DescriptionCorrectPropertyInformation !== null"
                  data-test="quickquote-house-description-correct-property-information"
                  class="sub-header"
                >
                  <span v-html="contentModel.DescriptionCorrectPropertyInformation" />
                </label>
              </div>
            </div>
            <div class="row">
              <div
                class="small-12 medium-6 columns padding-bottom padding-top"
                data-test="quickquote-house-bbr-details-home-construction-year"
              >
                <LabelAndTooltip
                  :label="contentModel.LabelHomeConstructionYear"
                  :tooltip="contentModel.TooltipHomeConstructionYear"
                />
                <div>
                  <strong><span v-html="quickQuoteStepModelData.houseDetails.ConstructionYear" /></strong>
                </div>
              </div>
              <div
                class="small-12 medium-6 columns padding-bottom padding-top"
                data-test="quickquote-house-bbr-details-number-of-toilets"
              >
                <LabelAndTooltip
                  :label="contentModel.LabelNumberOfToilets"
                  :tooltip="contentModel.TooltipNumberOfToilets"
                />
                <div>
                  <strong><span v-html="quickQuoteStepModelData.houseDetails.NumberOfToilets" /></strong>
                </div>
              </div>
            </div>
            <div class="row">
              <div
                class="small-12 medium-6 columns padding-bottom padding-top"
                data-test="quickquote-house-bbr-details-roof-type"
              >
                <LabelAndTooltip
                  :label="contentModel.LabelRoofType"
                  :tooltip="contentModel.TooltipRoofType"
                />
                <div>
                  <strong><span v-html="quickQuoteStepModelData.houseDetails.RoofType" /></strong>
                </div>
              </div>
              <div
                class="small-12 medium-6 columns padding-bottom padding-top"
                data-test="quickquote-house-bbr-details-exterior-walls"
              >
                <LabelAndTooltip
                  :label="contentModel.LabelExteriorWalls"
                  :tooltip="contentModel.TooltipExteriorWalls"
                />
                <div>
                  <strong><span v-html="quickQuoteStepModelData.houseDetails.ExteriorWalls" /></strong>
                </div>
              </div>
            </div>
            <div class="row">
              <div
                class="small-12 medium-6 columns padding-bottom padding-top"
                data-test="quickquote-house-bbr-details-heating-installation"
              >
                <LabelAndTooltip
                  :label="contentModel.LabelHeatingInstallation"
                  :tooltip="contentModel.TooltipHeatingInstallation"
                />
                <div>
                  <strong><span v-html="quickQuoteStepModelData.houseDetails.HeatingInstallation" /></strong>
                </div>
              </div>
              <div
                class="small-12 medium-6 columns padding-bottom padding-top"
                data-test="quickquote-house-bbr-details-home-size"
              >
                <LabelAndTooltip
                  :label="contentModel.LabelHomeSize"
                  :tooltip="contentModel.TooltipHomeSize"
                />
                <div>
                  <strong><span v-html="quickQuoteStepModelData.houseDetails.HouseSize" />
                    {{ contentModel.LabelSquareMeter }}</strong>
                </div>
              </div>
            </div>
            <div class="row">
              <div
                class="small-12 medium-6 columns padding-bottom padding-top"
                data-test="quickquote-house-bbr-details-basement-size"
              >
                <LabelAndTooltip
                  :label="contentModel.LabelBasementSize"
                  :tooltip="contentModel.TooltipBasementSize"
                />
                <div>
                  <strong><span v-html="quickQuoteStepModelData.houseDetails.BasementSize" />
                    {{ contentModel.LabelSquareMeter }}</strong>
                </div>
              </div>
              <div
                class="small-12 medium-6 columns padding-bottom padding-top"
                data-test="quickquote-house-bbr-details-outhouse-size"
              >
                <LabelAndTooltip
                  :label="contentModel.LabelOuthouseSize"
                  :tooltip="contentModel.TooltipOuthouseSize"
                />
                <div>
                  <strong><span v-html="quickQuoteStepModelData.houseDetails.OuthouseSize" />
                    {{ contentModel.LabelSquareMeter }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Andet om din ejendommen -->
        <div v-if="houseFound" class="control">
          <hr />
          <div class="row">
            <div class="small-12 columns">
              <h3-and-tooltip
                data-test="quickquote-house-headline-exstra-about-the-property"
                :h3="contentModel.LabelExstraAboutTheProperty"
                :tooltip="contentModel.TooltipExstraAboutTheProperty"
              />
            </div>
          </div>
          <!-- Er dit hus oprideligt opført til andet formål end beboelse -->
          <div v-if="showOuthouseSizeGreaterThan100">
            <div class="row">
              <div class="small-12 columns">
                <LabelAndTooltip
                  data-test="quickquote-house-LabelAndTooltip-built-for-other-purpose"
                  class="text-question"
                  :label="contentModel.LabelBuiltForOtherPurpose"
                  :tooltip="contentModel.TooltipBuiltForOtherPurpose"
                />
              </div>
            </div>
            <div class="row row-bottom-space">
              <div
                v-for="(radio, i) in yesNoRadioArray(
                  contentModel.SharedContent.LabelYes,
                  contentModel.SharedContent.LabelNo
                )"
                :key="'radio' + i"
                class="small-6 medium-3 columns"
              >
                <div
                  class="radio-stacked"
                  :data-test="'quickquote-house-input-built-for-other-purpose-' + radio.value"
                >
                  <Field
                    :id="'built-for-other-purpose' + radio.label"
                    v-model="quickQuoteStepModelData.builtForOtherPurpose"
                    :rules="{ required: quickQuoteStepModelData.builtForOtherPurpose === null }"
                    type="radio"
                    name="built-for-other-purpose"
                    :value="radio.value"
                    :label="contentModel.FieldNameForBuiltForOtherPurpose"
                  />
                  <label :for="'built-for-other-purpose' + radio.label">{{ radio.label }}</label>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="small-12 medium-6 columns">
                <ValidationError name="built-for-other-purpose" />
              </div>
            </div>
          </div>
          <!-- Er dit hus nedlagt landbrug -->
          <div v-if="showOuthouseSizeGreaterThan100">
            <div class="row">
              <div class="small-12 columns">
                <LabelAndTooltip
                  data-test="quickquote-house-LabelAndTooltip-discontinued-agriculture"
                  class="text-question"
                  :label="contentModel.LabelDiscontinuedAgriculture"
                  :tooltip="contentModel.TooltipDiscontinuedAgriculture"
                />
              </div>
            </div>
            <div class="row row-bottom-space">
              <div
                v-for="(radio, i) in yesNoRadioArray(
                  contentModel.SharedContent.LabelYes,
                  contentModel.SharedContent.LabelNo
                )"
                :key="'radio' + i"
                class="small-6 medium-3 columns"
              >
                <div
                  class="radio-stacked"
                  :data-test="'quickquote-house-input-discontinued-agriculture-' + radio.value"
                >
                  <Field
                    :id="'discontinued-agriculture' + radio.label"
                    v-model="quickQuoteStepModelData.discontinuedAgriculture"
                    :rules="{ required: quickQuoteStepModelData.discontinuedAgriculture === null }"
                    type="radio"
                    name="discontinued-agriculture"
                    :value="radio.value"
                    :label="contentModel.FieldNameForDiscontinuedAgriculture"
                  />
                  <label :for="'discontinued-agriculture' + radio.label">{{ radio.label }}</label>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="small-12 medium-6 columns">
                <ValidationError name="discontinued-agriculture" />
              </div>
            </div>
          </div>
          <!-- Er der erhvervsvirksomhed på ejendommen -->
          <div class="row">
            <div class="small-12 columns">
              <LabelAndTooltip
                data-test="quickquote-house-LabelAndTooltip-commercial-property"
                class="text-question"
                :label="contentModel.LabelIsCommercialProperty"
                :tooltip="contentModel.TooltipIsCommercialProperty"
              />
            </div>
          </div>
          <div class="row row-bottom-space">
            <div
              v-for="(radio, i) in yesNoRadioArray(
                contentModel.SharedContent.LabelYes,
                contentModel.SharedContent.LabelNo
              )"
              :key="'radio' + i"
              class="small-6 medium-3 columns"
            >
              <div
                class="radio-stacked"
                :data-test="'quickquote-house-input-commercial-property-' + radio.value"
              >
                <Field
                  :id="'commercial-property' + radio.label"
                  v-model="quickQuoteStepModelData.commercialProperty"
                  v:rules="{ required: quickQuoteStepModelData.commercialProperty === null }"
                  type="radio"
                  name="commercial-property"
                  :value="radio.value"
                  :label="contentModel.FieldNameForCommercialProperty"
                />
                <label :for="'commercial-property' + radio.label">{{ radio.label }}</label>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="small-12 medium-6 columns">
              <ValidationError name="commercial-property" />
            </div>
          </div>
          <!-- Har du indendørs swimmingpool -->
          <div class="row">
            <div class="small-12 columns">
              <LabelAndTooltip
                data-test="quickquote-house-LabelAndTooltip-indoor-swimming-pool"
                class="text-question"
                :label="contentModel.LabelHasIndoorSwimmingPool"
                :tooltip="contentModel.TooltipHasIndoorSwimmingPool"
              />
            </div>
          </div>
          <div class="row row-bottom-space">
            <div
              v-for="(radio, i) in yesNoRadioArray(
                contentModel.SharedContent.LabelYes,
                contentModel.SharedContent.LabelNo
              )"
              :key="'radio' + i"
              class="small-6 medium-3 columns"
            >
              <div
                class="radio-stacked"
                :data-test="'quickquote-house-input-indoor-swimming-pool-' + radio.value"
              >
                <Field
                  :id="'indoor-swimming-pool' + radio.label"
                  v-model="vueModel.TemplateModel.HasIndoorSwimmingPool"
                  :rules="{ required: vueModel.TemplateModel.HasIndoorSwimmingPool === null }"
                  type="radio"
                  name="indoor-swimming-pool"
                  :value="radio.value"
                  :label="contentModel.FieldNameForIndoorSwimmingPool"
                />
                <label :for="'indoor-swimming-pool' + radio.label">{{ radio.label }}</label>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="small-12 medium-6 columns">
              <ValidationError name="indoor-swimming-pool" />
            </div>
          </div>
          <!-- Er der monteret solceller på en eller flere bygninger -->
          <div class="row">
            <div class="small-12 columns">
              <LabelAndTooltip
                data-test="quickquote-house-LabelAndTooltip-solar-cells"
                class="text-question"
                :label="contentModel.LabelSolarCellsOnProperty"
                :tooltip="contentModel.TooltipSolarCellsOnProperty"
              />
            </div>
          </div>
          <div class="row row-bottom-space">
            <div
              v-for="(radio, i) in yesNoRadioArray(
                contentModel.SharedContent.LabelYes,
                contentModel.SharedContent.LabelNo
              )"
              :key="'radio' + i"
              class="small-6 medium-3 columns"
            >
              <div
                class="radio-stacked"
                :data-test="'quickquote-house-input-solar-cells-' + radio.value"
              >
                <Field
                  :id="'solar-cells' + radio.label"
                  v-model="vueModel.TemplateModel.HasSolarCellsOnProperty"
                  :rules="{ required: vueModel.TemplateModel.SolarCellsOnProperty === null }"
                  type="radio"
                  name="solar-cells"
                  :value="radio.value"
                  :label="contentModel.FieldNameForSolarCellsOnProperty"
                />
                <label :for="'solar-cells' + radio.label">{{ radio.label }}</label>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="small-12 medium-6 columns">
              <ValidationError name="solar-cells" />
            </div>
          </div>
        </div>

        <!-- Hvilke af disse trækonstrutioner findes i dit hus -->
        <div v-if="houseFound && hasWoodInConstruction" class="control">
          <hr />
          <div class="row">
            <div class="small-12 columns">
              <h3 data-test="quickquote-house-headline-house-type">
                <span v-html="contentModel.LabelHouseType" />
                <tooltip
                  v-if="contentModel.TooltipHouseType !== null"
                  :content="contentModel.TooltipHouseType"
                />
              </h3>
              <label
                v-if="contentModel.DescriptionHouseType !== null"
                data-test="quickquote-house-description-house-type"
                class="sub-header"
              >
                <span v-html="contentModel.DescriptionHouseType" />
              </label>
            </div>
          </div>
          <Field
            name="house-type"
            v-slot="{ field }"
            v-model="vueModel.TemplateModel.HouseTypeId"
            :rules="'min_value:1'"
          >
            <div
              v-for="(dropdownElement, i) in contentModel.TreelistHouseTypes.DropdownElements"
              :key="'house-type-' + i"
              class="row"
            >
              <div class="small-12 columns">
                <base-radio-button-single-card
                  :id="'house-type-' + i"
                  v-model="vueModel.TemplateModel.HouseTypeId"
                  :name="field.name"
                  :option="{
                    label: contentModel.TreelistHouseTypes.DropdownElements[i].Name,
                    description: contentModel.TreelistHouseTypes.DropdownElements[i].Description,
                    value: contentModel.TreelistHouseTypes.DropdownElements[i].Value
                  }"
                />
              </div>
            </div>
          </Field>
          <div class="row">
            <div class="small-12 columns">
              <ValidationError
                name="house-type"
                :overrides="{
                  min_value: contentModel.FieldNameHouseType + ' skal udfyldes.'
                }"
              />
            </div>
          </div>
        </div>

        <!-- Installationer i ejendommen -->
        <div v-if="houseFound" class="control">
          <hr />
          <div class="row">
            <div class="small-12 columns">
              <h3 data-test="quickquote-house-headline-installations">
                <span v-html="contentModel.LabelInstallationsOnTheProperty" />
                <tooltip
                  v-if="contentModel.TooltipInstallationsOnTheProperty !== null"
                  :content="contentModel.TooltipInstallationsOnTheProperty"
                />
              </h3>
              <label
                v-if="contentModel.DescriptionInstallationsOnTheProperty !== null"
                data-test="quickquote-house-description-installations-on-the-property"
                class="sub-header"
              >
                <span v-html="contentModel.DescriptionInstallationsOnTheProperty" />
              </label>
            </div>
          </div>
          <!-- Show only if the RoofType is Thatched. -->
          <div v-if="vueModel.TemplateModel.Asset.RoofType.Id == 1000011" class="row">
            <div class="small-12 columns">
              <base-single-checkbox-card
                v-model="vueModel.TemplateModel.IsApprovedFireRetardantMeasures"
                :option="{
                  label: contentModel.LabelApprovedFireRetardantMeasures,
                  description: contentModel.DescriptionApprovedFireRetardantMeasures
                }"
                name="approved-fire-retardant-measures"
              />
            </div>
          </div>
          <div class="row">
            <div class="small-12 columns">
              <base-single-checkbox-card
                v-model="vueModel.TemplateModel.IsApprovedWaterAlarm"
                :option="{
                  label: contentModel.LabelApprovedWaterAlarm,
                  description: contentModel.DescriptionApprovedWaterAlarm
                }"
                name="approved-water-alarm"
              />
            </div>
          </div>
          <div class="row">
            <div class="small-12 columns">
              <base-single-checkbox-card
                v-model="vueModel.TemplateModel.IsApprovedFloodProofingDevice"
                :option="{
                  label: contentModel.LabelApprovedFloodProofingDevice,
                  description: contentModel.DescriptionApprovedFloodProofingDevice
                }"
                name="approved-flood-proofing-device"
              />
            </div>
          </div>
        </div>

        <!-- Husets beboere -->
        <div v-if="houseFound" class="control">
          <hr />
          <div class="row">
            <div class="small-12 columns">
              <h3 data-test="quickquote-house-headline-age">
                <span v-html="contentModel.LabelAboutTheHouseOccupant" />
                <tooltip
                  v-if="contentModel.TooltipAboutTheHouseOccupant !== null"
                  :content="contentModel.TooltipAboutTheHouseOccupant"
                />
              </h3>
              <LabelAndTooltip
                data-test="quickquote-house-LabelAndTooltip-age"
                class="text-question"
                :label="contentModel.LabelContactAge"
                :tooltip="contentModel.TooltipContactAge"
              />
            </div>
          </div>
          <div class="row">
            <div class="small-12 medium-6 columns">
              <Field
                id="age"
                v-model="vueModel.TemplateModel.ContactAge"
                :rules="'required|min_value:18|max_value:110'"
                type="text"
                name="age"
                :label="contentModel.FieldNameContactAge"
                data-test="quickquote-house-input-age"
                :disabled="vueModel.IsKnownMember"
              />
            </div>
          </div>
          <div class="row">
            <div class="small-12 columns">
              <ValidationError name="age" />
            </div>
          </div>
          <!-- Lejer du huset ud -->
          <div class="row">
            <div class="small-12 columns">
              <LabelAndTooltip
                data-test="quickquote-house-LabelAndTooltip-has-sublease"
                class="text-question"
                :label="contentModel.LabelHasSublease"
                :tooltip="contentModel.TooltipHasSublease"
              />
            </div>
          </div>
          <div class="row">
            <div
              v-for="(radio, i) in yesNoRadioArray(
                contentModel.SharedContent.LabelYes,
                contentModel.SharedContent.LabelNo
              )"
              :key="'radio' + i"
              class="small-6 medium-3 columns"
            >
              <div
                class="radio-stacked"
                :data-test="'quickquote-house-input-has-sublease-' + radio.value"
              >
                <Field
                  :id="'has-sublease' + radio.label"
                  v-model="vueModel.TemplateModel.HasSublease"
                  :rules="{ required: vueModel.TemplateModel.HasSublease === null }"
                  type="radio"
                  name="has-sublease"
                  :value="radio.value"
                  :label="contentModel.FieldNameForHasSublease"
                />
                <label :for="'has-sublease' + radio.label">{{ radio.label }}</label>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="small-12 medium-6 columns">
              <ValidationError name="has-sublease" />
            </div>
          </div>
        </div>

        <!-- Questionnaire -->
        <div v-if="houseFound" class="control">
          <hr />
          <questionnaire
            :options="policyQuestions"
            :always-show="true"
            :heading-three="true"
            @questionnaire-updated="getQuestionnaireAnswersFromId"
          />
        </div>
      </article>
    </section>

    <!-- Særlige vilkår ved grønt tag - modal -->
    <transition name="modal">
      <v-modal
        :is-open="showGreenRoofModal"
        data-test="quickquote-house-green-roof-modal"
        modal-class="alt-modal"
        @close="showGreenRoofModal = false"
      >
        <template #title>
          <h2 class="center" v-html="contentModel.LabelGreenRoofModalHeadline" />
        </template>
        
        <template #content>
          <div v-html="contentModel.TextGreenRoofModalContent" />
        </template>
        <template #buttons>
          <div class="row">
            <div class="small-12 medium-6 columns">
              <a
                :href="contentModel.SharedContent.LinkCloseButton.Url"
                class="button tertiary"
                data-test="quickquote-house-green-roof-close-calculator-button"
                v-html="contentModel.LabelCloseCalculator"
              />
            </div>
            <div class="small-12 medium-6 columns">
              <button
                class="button text-center"
                data-test="quickquote-house-green-roof-stay-on-calculator-button"
                @click="showGreenRoofModal = false"
                v-html="contentModel.LabelStayOnCalculator"
              />
            </div>
          </div>
        </template>
      </v-modal>
    </transition>

    <!-- Særlige vilkår ved Bjælkehus, Bindingsværkshus eller Pudset træhus - modal -->
    <transition name="modal">
      <v-modal
        :is-open="showHouseTypeWoodModal"
        data-test="quickquote-house-type-wood-modal"
        modal-class="alt-modal"
        @close="showHouseTypeWoodModal = false"
      >
        <template #title>
          <h2 class="center" v-html="contentModel.LabelHouseTypeWoodModalHeadline" />
        </template>
        <template #content>
          <div v-html="contentModel.TextHouseTypeWoodModalContent" />
        </template>
        <template #buttons>
          <div class="row">
            <div class="small-12 medium-6 columns">
              <a
                :href="contentModel.SharedContent.LinkCloseButton.Url"
                class="button tertiary"
                data-test="quickquote-house-type-wood-close-calculator-button"
                v-html="contentModel.LabelCloseCalculator"
              />
            </div>
            <div class="small-12 medium-6 columns">
              <button
                class="button text-center"
                data-test="quickquote-house-type-wood-stay-on-calculator-button"
                @click="showHouseTypeWoodModal = false"
                v-html="contentModel.LabelStayOnCalculator"
              />
            </div>
          </div>
        </template>
      </v-modal>
    </transition>
  </div>
</template>

<script>
import { forEach } from 'lodash';
import { hasProperty } from '@/common/Util/utility';
import Utility from '@/common/UtilityFunctions.js';
import AddressSearch from '@/components/UI/AddressSearch.vue';

import H3AndTooltip from '@/components/UI/H3AndTooltip.vue';
import Tooltip from '@/components/UI/Tooltip.vue';
import BaseRadioButtonSingleCard from '@/components/Forms/BaseRadioButtonSingleCard.vue';
import BaseSingleCheckboxCard from '@/components/Forms/BaseSingleCheckboxCard.vue';
import ValidationError from '@/components/UI/ValidationError.vue';
import QuickQuoteMixin from './QuickQuoteMixin.vue';

export default {
  name: 'HouseQuickQuote1000079',
  components: {
    AddressSearch,
    
    H3AndTooltip,
    Tooltip,
    BaseRadioButtonSingleCard,
    BaseSingleCheckboxCard,
    ValidationError
  },
  emits: ['disable-next-button', 'update-personal-information'],
  mixins: [QuickQuoteMixin],
  props: {
    contentModel: {
      type: Object,
      default() {
        return {};
      }
    },
    vueModel: {
      type: Object,
      default() {
        return {};
      }
    },
    stepModel: {
      type: Object,
      default() {
        return {};
      }
    },
    isQqErrorModalShown: {
      type: Boolean,
      default() {
        return false;
      }
    },
    quickQuoteStepModel: {
      type: Object,
      default() {
        return {
          builtForOtherPurpose: false,
          discontinuedAgriculture: false,
          commercialProperty: false,
          houseDetails: {
            HouseSize: null,
            OuthouseSize: null,
            BasementSize: null,
            ConstructionYear: null,
            RoofTypeId: null,
            RoofType: null,
            NumberOfToilets: null,
            NumberOfFloors: null,
            ExteriorWalls: null,
            HeatingInstallation: null,
            OuterWallsTypeId: null,
            ActualCommercialUseArea: null
          }
        };
      }
    }
  },
  data() {
    return {
      isBlocked: false,
      showGreenRoofModal: false,
      hasWoodInConstruction: false,
      showHouseTypeWoodModal: false,
      alreadyShowHouseTypeWoodModal: false,
      quickQuoteStepModelData: {
        type: Object,
        default() { return null; }
      }
    };
  },
  beforeMount() {
    this.quickQuoteStepModelData = this.quickQuoteStepModel;
    this.setQuickQuoteStepModel();
  },
  computed: {
    houseFound() {
      //debugger;
      return this.quickQuoteStepModelData.houseDetails?.HouseSize !== null;
    },
    showOuthouseSizeGreaterThan100() {
      return this.quickQuoteStepModelData.houseDetails?.OuthouseSize > 100;
    },
    personalInformationForTagManager() {
      return {
        event: 'track-event',
        eventCategory: this.getDanishProductTypeName + ' prisberegner',
        eventAction: '01 oplysninger submit ok',
        eventLabel: 'oplysninger submit ok',
        eventValue: '0',
        eventNoninteraction: true,
        data: {
          houseYear: this.quickQuoteStepModelData.houseDetails?.ConstructionYear
        }
      };
    },
    policyQuestionDamagesLastThreeYears() {
      if (
        this.vueModel.TemplateModel.PolicyQuestionnaire === undefined ||
        this.vueModel.TemplateModel.PolicyQuestionnaire === null
      ) {
        return null;
      }
      return this.vueModel.TemplateModel.PolicyQuestionnaire.Questionnaires.filter(
        (el) => el.LineId == 1002134
      )[0];
    }
  },
  watch: {
    'policyQuestionDamagesLastThreeYears.ChosenAnswer': function (val) {
      if (val == 1001155) {
        this.$emit('disable-next-button', true);
        this.showErrorModal();
      } else {
        this.updateBlockStatus();
      }
    },
    'vueModel.TemplateModel.HouseUsageId': function (val) {
      if (val == 1000001 || val == 1000008) {
        this.$emit('disable-next-button', true);
        this.showErrorModal();
      } else {
        this.updateBlockStatus();
      }
    },
    'vueModel.TemplateModel.HousePropertyTypeId': function (val) {
      if (val == 1000005) {
        this.$emit('disable-next-button', true);
        this.showErrorModal();
      } else {
        this.updateBlockStatus();
      }
    },
    'quickQuoteStepModelData.commercialProperty': function (val) {
      this.stepModel.commercialProperty = val;
      if (val) {
        this.$emit('disable-next-button', true);
        this.showErrorModal();
      } else {
        this.updateBlockStatus();
      }
    },
    'quickQuoteStepModelData.builtForOtherPurpose': function (val) {
      this.stepModel.builtForOtherPurpose = val;
      if (val) {
        this.$emit('disable-next-button', true);
        this.showErrorModal();
      } else {
        this.updateBlockStatus();
      }
    },
    'quickQuoteStepModelData.discontinuedAgriculture': function (val) {
      this.stepModel.discontinuedAgriculture = val;
      if (val) {
        this.$emit('disable-next-button', true);
        this.showErrorModal();
      } else {
        this.updateBlockStatus();
      }
    },
    'vueModel.TemplateModel.IsApprovedFireRetardantMeasures': function (val) {
      // We should only show a warning box if the rooftype is
      if (val) {
        this.$emit('disable-next-button', true);
        this.showErrorModal();
      } else {
        this.updateBlockStatus();
      }
    },
    'vueModel.TemplateModel.SelectedAddressTemplate': function () {
      this.updateQuickQuoteAsset(
        (response) => {
          this.updateHouseDetails(response);
          this.responseSuccess(response);
          Utility.pushToTagManager({
            event: 'track-ga-vp',
            virtualPath: '/virtual/engagement/hus-prisberegner-02-adressefelt-ok',
            virtualTitle: 'Virtuel - Beregn pris - Hus - adressefelt OK'
          });
        },
        () => {
          // this.$parent.$emit('showStdErrorModal');
          this.$parent.$parent.$parent.showQuickQuoteModalErrorValidation = false;
          this.$parent.$parent.$parent.showQuickQuoteModal = true;
          Utility.pushToTagManager({
            event: 'track-event',
            eventCategory: 'Hus prisberegner',
            eventAction: '01 oplysninger søg adresse error',
            eventLabel: '',
            eventValue: '0',
            eventNoninteraction: true
          });
        }
      );
    },
    'quickQuoteStepModelData.houseDetails.ActualCommercialUseArea': function (val) {
      if (val > 0) {
        this.$emit('disable-next-button', true);
        this.showErrorModal();
      } else {
        this.updateBlockStatus();
      }
    },
    'quickQuoteStepModelData.houseDetails.OuterWallsTypeId': function (val) {
      if (val == 1000005 || val == 1000006) {
        this.hasWoodInConstruction = true;
      }
      if (val === 1000011 || val === 1000012) {
        this.$emit('disable-next-button', true);
        this.showErrorModal();
      } else {
        this.updateBlockStatus();
      }
    },
    'vueModel.TemplateModel.HouseTypeId': function (val) {
      if (val == 1000001 || val == 1000002 || val == 1000003) {
        if (!this.alreadyShowHouseTypeWoodModal && val > 0) {
          this.alreadyShowHouseTypeWoodModal = true;
          this.showHouseTypeWoodModal = true;
        }
      }
    },
    'quickQuoteStepModelData.houseDetails.RoofTypeId': function (val) {
      if (val == 1000015 && !this.isQqErrorModalShown && !this.showHouseTypeWoodModal) {
        this.showGreenRoofModal = true;
      } else if (val === 1000016 || val === 1000017) {
        this.$emit('disable-next-button', true);
        this.showErrorModal();
      } else {
        this.updateBlockStatus();
      }
    },
    personalInformationForTagManager() {
      this.$emit('update-personal-information', this.personalInformationForTagManager);
    }
  },
  mounted() {
    this.setQuickQuoteStepModel();
  },
  methods: {
    yesNoRadioArray(yes, no) {
      return [
        {
          value: true,
          label: yes
        },
        {
          value: false,
          label: no
        }
      ];
    },
    updateHouseDetails(response) {
      if (hasProperty(response, 'Asset')) {
        this.hasWoodInConstruction = false;
        this.showHouseTypeWoodModal = false;
        this.alreadyShowHouseTypeWoodModal = false;

        this.quickQuoteStepModelData.houseDetails.HouseSize = response.Asset.LivingArea;
        this.quickQuoteStepModelData.houseDetails.OuthouseSize = response.Asset.AdditionalBuildingsArea;
        this.quickQuoteStepModelData.houseDetails.BasementSize = response.Asset.BasementArea;
        this.quickQuoteStepModelData.houseDetails.ConstructionYear = response.Asset.YearOfConstruction;
        this.quickQuoteStepModelData.houseDetails.RoofTypeId = response.Asset.RoofType.Id;
        this.quickQuoteStepModelData.houseDetails.RoofType = response.Asset.RoofType.Description;
        this.quickQuoteStepModelData.houseDetails.NumberOfToilets =
          response.Asset.NumberOfToilets ?? 'N/A';
        this.quickQuoteStepModelData.houseDetails.NumberOfFloors =
          response.Asset.NumberOfFloors ?? 'N/A';
        this.quickQuoteStepModelData.houseDetails.ExteriorWalls = response.Asset.ExteriorWalls ?? 'N/A';
        this.quickQuoteStepModelData.houseDetails.HeatingInstallation =
          response.Asset.HeatingInstallation ?? 'N/A';
        this.quickQuoteStepModelData.houseDetails.OuterWallsTypeId = response.Asset.OuterWallsTypeId;
        this.quickQuoteStepModelData.houseDetails.ActualCommercialUseArea =
          response.Asset.ActualCommercialUseArea;

        this.stepModel.houseDetails = this.quickQuoteStepModelData.houseDetails;

        // Skip clearing the quick quote on next as it is handled in this step.
        this.vueModel.TemplateModel.ShouldClearAsset = false;
        this.vueModel.TemplateModel.HouseUsageId = response.Asset.HouseUsageId;
        this.vueModel.TemplateModel.HouseTypeId = 0;
        this.vueModel.TemplateModel.ListedCategoryTypeId = response.Asset.ListedCategoryTypeId;
      }
    },
    responseSuccess(response) {
      this.handleResponse(
        response,
        () => {
          this.$parent.$emit('showQuickQuoteModalErrorValidation', true);
        },
        false
      );
    },
    setQuickQuoteStepModel() {
      const self = this;
      forEach(this.stepModel, (val, key) => {
        self.quickQuoteStepModelData[key] = val;
      });
    },
    updateBlockStatus() {
      if (
        !this.isBlocked &&
        !this.quickQuoteStepModelData.commercialProperty &&
        !this.quickQuoteStepModelData.builtForOtherPurpose &&
        !this.quickQuoteStepModelData.discontinuedAgriculture &&
        !this.vueModel.TemplateModel.IsApprovedFireRetardantMeasures &&
        this.vueModel.TemplateModel.HousePropertyTypeId != 1000005 &&
        this.vueModel.TemplateModel.HouseUsageId != 1000001 &&
        this.vueModel.TemplateModel.HouseUsageId != 1000008 &&
        this.quickQuoteStepModelData.houseDetails.RoofTypeId != 1000016 &&
        this.quickQuoteStepModelData.houseDetails.RoofTypeId != 1000017 &&
        this.quickQuoteStepModelData.houseDetails.OuterWallsTypeId != 1000011 &&
        this.quickQuoteStepModelData.houseDetails.OuterWallsTypeId != 1000012 &&
        this.quickQuoteStepModelData.houseDetails.ActualCommercialUseArea == 0
      ) {
        this.$emit('disable-next-button', false);
      }
    },
    getQuestionnaireAnswersFromId(param) {
      if (param.answer.Block) {
        this.$emit('disable-next-button', true);
        this.showErrorModal();
      }
      this.isBlocked = param.blockStatus;
      this.updateBlockStatus();
    },
    activityInAddressField() {
      Utility.pushToTagManager({
        event: 'track-ga-vp',
        virtualPath: '/virtual/engagement/hus-prisberegner-01-adressefelt-forladt',
        virtualTitle: 'Virtuel - Beregn pris - Hus - adressefelt aktivitet'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/styles/all.scss';
@import 'node_modules/foundation-sites/scss/util/util';

.bbr-section {
  margin-top: rem-calc(50);

  label {
    color: #696969;

    &.sub-header {
      color: $text;
    }
  }

  strong {
    color: $text;
  }

  .row-bottom-space {
    margin-bottom: rem-calc(15);
  }
}

.sub-header {
  margin-bottom: 1rem;
  color: $text;
}

h2.center {
  text-align: center;
}
</style>
